import React from 'react';
import { useSelector } from 'react-redux';
import { generatePath } from 'react-router';
import { Redirect, Route, RouteComponentProps, Switch, useLocation } from 'react-router-dom';

import { TargetingValues } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/apis';
import { ErrorWrapper } from '@provider-portal/common/components/ErrorWrapper/ErrorWrapper';
import { defaultRoute } from '@provider-portal/common/routing/routes';
import { useIsSelectedProviderInOnboarding } from '@provider-portal/common/hooks/useIsSelectedProviderInOnboarding';
import { AccountPermissions, AccountStage } from '@provider-portal/common/services/types/account';
import { isEqual } from 'lodash';
import queryString from 'query-string';
import { Facade } from '@bolteu/lattice';
import { cx } from '@bolteu/clay-react';

import {
  selectAccountStageOrNewMerchantVenueOnboarding,
  selectIsAccountMisconfigured,
  selectSelectedProviderPermissions,
} from 'rdx/account/selectors';
import { PageChangeTracker } from 'services/analytics/PageChangeTracker';

import { AccessControl } from 'components/AccessControl/AccessControl';
import { AccountMisconfiguredPage } from 'components/AccountMisconfigured/AccountMisconfiguredPage';
import { GenericErrorPage } from 'components/GenericErrorPage/GenericErrorPage';
import { NavBar as MerchantPortal2NavBar } from 'components/merchant-portal-2/NavBar/NavBar';
import {
  buildMenuFromConfigAndModules,
  getFilteredTabHeaderForRoute,
  MenuContext,
} from 'root/navigation/navStructureUtils';
import { AccountUnlock } from 'screens/AccountUnlock/AccountUnlock';
import { ForgotPassword } from 'screens/ForgotPassword/ForgotPassword';
import { Login } from 'screens/Login/Login';
import { MainContent } from 'screens/MainContent/MainContent';
import { ConfigMenu } from './navigation/navTypes';
import { useHasPermanentNavBar } from 'components/merchant-portal-2/NavBar/useHasPermanentNavBar';

import { AppRoutes } from './routing/AppRoutes';

export { AppRoutes } from './routing/AppRoutes';

export const BackwardsCompatibleRedirects = [
  // Redir to the lattice module's path
  { from: '/menu/providerDetails', to: '/management/provider-details' },
];

const deductionsDefaultRoute = '/deductions/list';

export const AuthenticationRoutes = () => {
  const renderCallback = ({ location }: RouteComponentProps) => {
    const parsedQuery = queryString.parse(location.search);
    const to = parsedQuery.to || defaultRoute;

    const query = queryString.stringify({ to });
    const redirectPath = generatePath(AppRoutes.AUTH, {});
    return <Redirect to={`${redirectPath}?${query}`} />;
  };

  return (
    <Switch>
      <Route path={AppRoutes.AUTH} key={AppRoutes.AUTH}>
        <Login />
      </Route>
      <Route exact path={AppRoutes.FORGOT_PASSWORD} key={AppRoutes.FORGOT_PASSWORD}>
        <ForgotPassword />
      </Route>
      <Route exact path={AppRoutes.UNLOCK_ACCOUNT} key={AppRoutes.UNLOCK_ACCOUNT}>
        <AccountUnlock />
      </Route>
      <Route path="*" render={renderCallback} key="unauthenticated-redirect" />
    </Switch>
  );
};

interface ProtectedRoutesProps {
  moduleManifests: Facade.ModuleManifest[];
  targetingParameters: TargetingValues;
  children: React.ReactNode;
}

export function ProtectedRoutes({
  children,
  moduleManifests,
  targetingParameters,
}: ProtectedRoutesProps) {
  const currentLocation = useLocation();

  const isAccountMisconfigured = useSelector(selectIsAccountMisconfigured);
  const providerPermissions = useSelector(selectSelectedProviderPermissions, isEqual);
  const isAccountOnboarding =
    useSelector(selectAccountStageOrNewMerchantVenueOnboarding) === AccountStage.ONBOARDING;
  const isSelectedProviderInOnboarding = useIsSelectedProviderInOnboarding();
  const selectedUserABTestVariantName =
    targetingParameters?.delivery_provider_portal_flags?.user_ab_test_experiments ?? 'control';
  // DPX-2882: temporary solution before proper permissions system to only show specific page(s) for specific role
  const exclusivePermissions: Partial<AccountPermissions> = {
    deductionsAccess:
      targetingParameters?.delivery_provider_portal_flags?.permission_deductions_only,
  };

  const menuItems = buildMenuFromConfigAndModules(
    moduleManifests,
    providerPermissions,
    exclusivePermissions,
    targetingParameters,
    isAccountOnboarding,
    { name: 'user_ab_test_experiments', variant: selectedUserABTestVariantName },
  );

  const activeTabHeader = getFilteredTabHeaderForRoute(
    currentLocation.pathname,
    providerPermissions,
    targetingParameters,
    isSelectedProviderInOnboarding,
  );

  const renderCallback = ({ location }: RouteComponentProps) => {
    if (exclusivePermissions.deductionsAccess) {
      return <Redirect to={deductionsDefaultRoute} />;
    }

    const query = queryString.parse(location.search);
    return <Redirect to={String(query.to || defaultRoute)} />;
  };

  const hasPermanentNavBar = useHasPermanentNavBar();

  // The account has no venues, so nothing below can resolve a selected provider.
  // Returning here keeps the NavBar and modules from mounting at all.
  if (isAccountMisconfigured) {
    return <AccountMisconfiguredPage />;
  }

  const menuContextObject: ConfigMenu = {
    menu: menuItems,
    activeMenuEntryIdOverride: activeTabHeader?.associatedMenuEntryId,
  };

  return (
    <Route path={AppRoutes.HOME} key={AppRoutes.HOME}>
      <MenuContext.Provider value={menuContextObject}>
        <AccessControl>
          <PageChangeTracker />

          <MerchantPortal2NavBar />

          <ErrorWrapper
            fallbackElement={
              <main className="mainContent">
                <div className={cx('px-8 py-6', hasPermanentNavBar && 'ml-[228px]')}>
                  <div>
                    <div className="mt-10">
                      <GenericErrorPage pageHeight="80vh" />
                    </div>
                  </div>
                </div>
              </main>
            }
          >
            <MainContent activeTabHeader={activeTabHeader}>{children}</MainContent>
          </ErrorWrapper>
          <Route
            path={[AppRoutes.AUTH, AppRoutes.HOME]}
            exact
            render={renderCallback}
            key="unauthenticated-redirect"
          />

          {BackwardsCompatibleRedirects.map((redir) => (
            <Route path={redir.from} key={redir.from}>
              <Redirect to={redir.to} />
            </Route>
          ))}
        </AccessControl>
      </MenuContext.Provider>
    </Route>
  );
}
